import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  TextField,
  MenuItem,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
  AlertTitle,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useParams } from "react-router-dom";
import { API_ENDPOINT } from "../../utils/constants";
import { getErrorResponse, toastError } from "../../utils/util";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";

const WelcomeInviteMember = () => {
  const { id } = useParams(); // The token from URL params

  const [profileImage, setProfileImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [upLoading, setUpLoading] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState(null);
  const [verificationError, setVerificationError] = useState(null);

  // Verify token when component mounts
  useEffect(() => {
    const verifyToken = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${API_ENDPOINT}user/verify-member-invite-token?id=${id}`
        );
        setVerificationMessage(data.message);
        loadDesignations(id);
      } catch (error) {
        setVerificationError(getErrorResponse(error));
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [id]);

  async function loadDesignations(token) {
    try {
      const { data } = await axios.get(
        `${API_ENDPOINT}user/get-designations-member?id=${token}`
      );
      setRoles(data);
    } catch (error) {
      toastError(error);
    }
  }

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const imageUrl = URL.createObjectURL(file);
      setProfileImage(imageUrl);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim() || !role || !profileImage) {
      toast.error("All fields are required.");
      return;
    }
    try {
      setUpLoading(true);
      const formData = new FormData();
      formData.append("image", selectedFile); // The selected image file
      formData.append("name", name);
      formData.append("role", role);
      const { data } = await axios.post(
        `${API_ENDPOINT}user/register-member?id=${id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setRequestSuccess(true);
      toast.success(data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setUpLoading(false);
    }

    // Handle submission logic (API call, etc.)
  };

  return requestSuccess ? (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh" // Full viewport height
    >
      <Alert severity="success">
        <AlertTitle>Profile Created Successfully</AlertTitle>
        Your profile has been created successfully! You can now log in to the{" "}
        <strong>BetaZen App</strong>. Before logging in, please set your account
        password using the link sent to your email.
        <strong>Check your inbox</strong> and follow the instructions to
        complete the setup.
      </Alert>
    </Box>
  ) : (
    <Container maxWidth="sm">
      <Card sx={{ mt: 5, p: 3, textAlign: "center", boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Welcome to BetaZen!
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Join our community and set up your profile to get started.
          </Typography>

          {/* Loading Indicator while Verifying Token */}
          {loading && (
            <Box display="flex" justifyContent="center" my={2}>
              <CircularProgress />
            </Box>
          )}

          {/* Verification Success/Error Messages
          {verificationMessage && (
            <Alert severity="success">{verificationMessage}</Alert>
          )} */}
          {verificationError && (
            <Alert severity="error">{verificationError}</Alert>
          )}

          {/* Hide form if token is invalid */}
          {!loading && !verificationError && (
            <>
              {/* Profile Image Upload */}
              <Stack spacing={2} alignItems="center">
                <Avatar
                  src={profileImage}
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: "primary.light",
                    fontSize: "2rem",
                  }}
                >
                  {!profileImage && "?"}
                </Avatar>
                <Button
                  size="small"
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                >
                  Upload Profile Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
              </Stack>

              {/* Name Input */}
              <TextField
                size="small"
                fullWidth
                label="Name"
                variant="outlined"
                sx={{ mt: 3 }}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              {/* Role Selection */}
              <TextField
                size="small"
                fullWidth
                select
                label="Designation"
                variant="outlined"
                sx={{ mt: 2 }}
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {roles?.map((option) => (
                  <MenuItem key={option._id} value={option.designation}>
                    {option.designation}
                  </MenuItem>
                ))}
              </TextField>

              {/* Submit Button */}
              <LoadingButton
                fullWidth
                loading={upLoading}
                variant="contained"
                sx={{ mt: 3 }}
                onClick={handleSubmit}
                disabled={!name || !role || !profileImage}
              >
                Join BetaZen
              </LoadingButton>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default WelcomeInviteMember;
